import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

interface Rac {
  id: string;
  rac: string;
  gds: string;
  createdBy: string;
}

interface Property {
  id: string;
  propertyName: string;
  propertyCity: string | null;
  propertyState: string | null;
  vendorFamily: string | null;
  status: string;
  notes: string | null;
}

interface Rate {
  id: string;
  propertyId: string;
  rac: string;
  gds: string;
  rateAmount: string;
  currency: string;
  validFrom: string;
  validTo: string;
  property: { propertyName: string; vendorFamily: string | null } | null;
}

// STATUS_BADGES went with the Hotel properties table (item 48) — it coloured
// that table's Status column and had no other reader. The canonical
// status→badge map for agreements lives in features/hotel-supplier/
// agreement-status.ts; this was a separate, property-status one.

// Renamed from BtpPanel — the user-facing feature is "Hotel Supplier Management".
// Internal API + DB names (capability:write:supplier, schema hsp, btp_enabled) kept
// because they're identifiers, not labels.
export function HotelSupplierPanel({ customerId }: { customerId: string }) {
  const [racs, setRacs] = useState<Rac[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [rates, setRates] = useState<Rate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newRac, setNewRac] = useState('');
  const [newGds, setNewGds] = useState('TP+');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [r1, r2, r3] = await Promise.all([
        apiFetch<{ racs: Rac[] }>(
          `/api/btp/racs?customer_id=${encodeURIComponent(customerId)}`,
        ),
        apiFetch<{ properties: Property[] }>(
          `/api/btp/properties?customer_id=${encodeURIComponent(customerId)}`,
        ),
        apiFetch<{ rates: Rate[] }>(
          `/api/btp/rates?customer_id=${encodeURIComponent(customerId)}`,
        ),
      ]);
      setRacs(r1.racs);
      setProperties(r2.properties);
      setRates(r3.rates);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const counts = useMemo(() => {
    const byStatus = new Map<string, number>();
    for (const p of properties)
      byStatus.set(p.status, (byStatus.get(p.status) ?? 0) + 1);
    const expiringSoon = rates.filter(
      (r) => new Date(r.validTo).getTime() < Date.now() + 90 * 86_400_000,
    ).length;
    return {
      contracted: byStatus.get('contracted') ?? 0,
      inFlight:
        (byStatus.get('rfp_sent') ?? 0) +
        (byStatus.get('response_received') ?? 0),
      identified: byStatus.get('identified') ?? 0,
      expiring: expiringSoon,
    };
  }, [properties, rates]);

  const addRac = async () => {
    const rac = newRac.trim().toUpperCase();
    if (!rac) return;
    try {
      await apiFetch('/api/btp/racs', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, rac, gds: newGds }),
      });
      setNewRac('');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <div className="loading-text">Loading Hotel Supplier Management…</div>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="banner banner-error">{error}</div>}

      <div className="kpi-row kpi-row-4">
        <div className="kpi-card kpi-highlight">
          <span className="kpi-label">Contracted</span>
          <span className="kpi-value">{counts.contracted}</span>
          <span className="kpi-sub">live negotiated rates</span>
        </div>
        <div className="kpi-card kpi-amber">
          <span className="kpi-label">In flight</span>
          <span className="kpi-value">{counts.inFlight}</span>
          <span className="kpi-sub">RFPs out or awaiting decision</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Identified</span>
          <span className="kpi-value">{counts.identified}</span>
          <span className="kpi-sub">not yet kicked off</span>
        </div>
        <div className="kpi-card kpi-red">
          <span className="kpi-label">Expiring · 90d</span>
          <span className="kpi-value">{counts.expiring}</span>
          <span className="kpi-sub">candidates for renewal cycle</span>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Rate Access Codes</span>
          <span className="section-meta">{racs.length} active</span>
        </div>
        <div className="section-body">
          <p
            style={{
              fontSize: 'var(--fs-base)',
              color: 'var(--text-secondary)',
              marginBottom: 12,
            }}
          >
            Required for the GDS-loading step of the supplier-rate loop. Each
            RAC corresponds to one GDS family.
          </p>
          {racs.length === 0 ? (
            <div
              style={{
                fontSize: 'var(--fs-base)',
                color: 'var(--text-muted)',
                fontStyle: 'italic',
                marginBottom: 12,
              }}
            >
              No RACs yet — add the first one below.
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 14,
              }}
            >
              {racs.map((r) => (
                <span
                  key={r.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'var(--accent-light)',
                    border: '1px solid var(--accent-soft)',
                    borderRadius: 'var(--radius-pill)',
                    padding: '5px 12px',
                    fontSize: 'var(--fs-base)',
                    color: 'var(--accent-darker)',
                    fontFamily: 'SF Mono, Menlo, monospace',
                    fontWeight: 700,
                    boxShadow: 'var(--shadow-1)',
                  }}
                >
                  {r.rac}
                  <span
                    style={{
                      color: 'var(--text-muted)',
                      fontWeight: 500,
                      fontFamily: 'var(--font-family-inter)',
                    }}
                  >
                    {r.gds}
                  </span>
                </span>
              ))}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-end',
              borderTop: '1px solid var(--border-light)',
              paddingTop: 14,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '0 0 160px' }}>
              <label className="field-label">RAC</label>
              <input
                value={newRac}
                onChange={(e) => setNewRac(e.target.value.toUpperCase())}
                placeholder="0FR"
                className="input"
                style={{
                  fontFamily: 'SF Mono, Menlo, monospace',
                  textTransform: 'uppercase',
                }}
              />
            </div>
            <div style={{ flex: '0 0 160px' }}>
              <label className="field-label">GDS</label>
              <select
                value={newGds}
                onChange={(e) => setNewGds(e.target.value)}
                className="select"
              >
                <option>TP+</option>
                <option>Sabre</option>
                <option>Amadeus</option>
                <option>Worldspan</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={addRac}>
              Add RAC
            </button>
          </div>
        </div>
      </div>

      {/*
       * HOTEL PROPERTIES WAS REMOVED HERE — feedback item 48, sequenced after 55.
       *
       * It listed `crm.customer_property` for one customer and carried its own
       * "Add property" form taking a name, a city and a chain. Stacie asked for
       * it gone because the Hotels tab is the place properties are worked, and
       * two surfaces for one table is how they drift.
       *
       * WHY IT HAD TO GO *WITH* ITEM 55 RATHER THAN BEFORE OR AFTER. That form
       * posted to `POST /api/btp/properties` with NO GDS property code. As of
       * item 55 that route refuses (422) unless the code resolves to exactly one
       * `hotel_master` row — so this form could no longer succeed at all. Left
       * in place it would be a button that always errors; removed earlier, it
       * would have hidden the orphaned rows before the write that created them
       * was fixed. The plan's ordering ("A6 must be last") was right for a
       * reason that only became visible once A1 landed.
       *
       * Properties are added through the agreement form, which asks for the code
       * and can therefore link them to the master.
       */}

      <div className="section">
        <div className="section-header green">
          <span className="section-label">Negotiated rates</span>
          <span className="section-meta">{rates.length} active</span>
        </div>
        <div className="section-body">
          {rates.length === 0 ? (
            <div
              style={{
                fontSize: 'var(--fs-base)',
                color: 'var(--text-muted)',
                fontStyle: 'italic',
              }}
            >
              No rates contracted yet — add a property + run an RFP cycle.
            </div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>RAC / GDS</th>
                    <th className="right">Rate</th>
                    <th>Valid</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r) => (
                    <tr key={r.id}>
                      <td>{r.property?.propertyName || '—'}</td>
                      <td style={{ fontFamily: 'SF Mono, Menlo, monospace' }}>
                        {r.rac}{' '}
                        <span style={{ color: 'var(--text-faint)' }}>
                          / {r.gds}
                        </span>
                      </td>
                      <td className="right" style={{ fontWeight: 600 }}>
                        {r.currency} {Number(r.rateAmount).toFixed(2)}
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {fmtDate(r.validFrom)} → {fmtDate(r.validTo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
